import React from "react";
import { View, Linking } from "react-native";
import { Controller } from "react-hook-form";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { StepShell } from "../components/StepShell";
import { AppTextInput } from "@/components/ui/TextInput";
import { FileUpload } from "@/components/ui/FileUpload";
import { usePreVettingStep2Form } from "../hooks/usePreVettingStep2Form";
import type { TranslationKeys } from "@chinese-vendor/i18n";

export default function PreVettingStep2Screen() {
  const { t } = useI18n();
  const {
    form,
    onFieldBlur,
    handleNext,
    handleBack,
    businessLicenseFile,
    businessLicenseUploadState,
    businessLicenseUploadError,
    handleBusinessLicenseFile,
    handleBusinessLicenseRemove,
  } = usePreVettingStep2Form();

  const { errors } = form.formState;

  return (
    <StepShell
      step={2}
      onNext={handleNext}
      onBack={handleBack}
      nextLabel="preVetting.step2.nextLabel"
      iconName="office-building"
      title="preVetting.step2.title"
      subtitle="preVetting.step2.subtitle"
    >
      <View className="items-start">
        <View className="tablet:flex-row tablet:gap-4 w-full">
          <View className="flex-1">
            <Controller
              control={form.control}
              name="company_name_chinese"
              render={({ field: { value, onChange, onBlur } }) => (
                <AppTextInput
                  required
                  label="preVetting.step2.companyNameChinese"
                  value={value}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  error={errors.company_name_chinese?.message as TranslationKeys | undefined}
                />
              )}
            />
          </View>

          <View className="flex-1">
            <Controller
              control={form.control}
              name="company_name_english"
              render={({ field: { value, onChange, onBlur } }) => (
                <AppTextInput
                  required
                  label="preVetting.step2.companyNameEnglish"
                  value={value}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  error={errors.company_name_english?.message as TranslationKeys | undefined}
                />
              )}
            />
          </View>
        </View>

        <Controller
          control={form.control}
          name="company_name_arabic"
          render={({ field: { value, onChange, onBlur } }) => (
            <AppTextInput
              label="preVetting.step2.companyNameArabicOptional"
              value={value ?? ""}
              onChangeText={onChange}
              onBlur={() => {
                onBlur();
                onFieldBlur();
              }}
              isRTL
            />
          )}
        />

        <View className="h-[1px] bg-stroke-50 my-8 w-full" />

        {/* ─── Headquarters Address section ───────────────────────── */}
        <AppText
          weight="bold"
          className="text-xs text-dark-text-15 uppercase tracking-widest mt-2 mb-4"
        >
          {t("preVetting.step2.sectionHqAddress")}
        </AppText>

        <Controller
          control={form.control}
          name="hq_address"
          render={({ field: { value, onChange, onBlur } }) => (
            <AppTextInput
              required
              label="preVetting.step2.hqAddress"
              value={value}
              onChangeText={onChange}
              onBlur={() => {
                onBlur();
                onFieldBlur();
              }}
              error={errors.hq_address?.message as TranslationKeys | undefined}
            />
          )}
        />

        <View className="tablet:flex-row tablet:gap-4 w-full">
          <View className="flex-1">
            <Controller
              control={form.control}
              name="hq_city"
              render={({ field: { value, onChange, onBlur } }) => (
                <AppTextInput
                  required
                  label="preVetting.step2.hqCity"
                  value={value}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  error={errors.hq_city?.message as TranslationKeys | undefined}
                />
              )}
            />
          </View>

          <View className="flex-1">
            <Controller
              control={form.control}
              name="hq_postal_code"
              render={({ field: { value, onChange, onBlur } }) => (
                <AppTextInput
                  required
                  label="preVetting.step2.hqPostalCode"
                  value={value}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  keyboardType="number-pad"
                  error={errors.hq_postal_code?.message as TranslationKeys | undefined}
                />
              )}
            />
          </View>
        </View>

        <Controller
          control={form.control}
          name="unified_social_credit_code"
          render={({ field: { value, onChange, onBlur } }) => (
            <AppTextInput
              required
              label="preVetting.step2.unifiedSocialCreditCode"
              value={value}
              onChangeText={onChange}
              onBlur={() => {
                onBlur();
                onFieldBlur();
              }}
              autoCapitalize="characters"
              maxLength={18}
              error={errors.unified_social_credit_code?.message as TranslationKeys | undefined}
            />
          )}
        />

        <FileUpload
          required
          label="preVetting.step2.businessLicense"
          onFile={handleBusinessLicenseFile}
          uploadState={businessLicenseUploadState}
          currentFile={businessLicenseFile}
          onViewFile={
            businessLicenseFile ? () => Linking.openURL(businessLicenseFile.uri) : undefined
          }
          onRemoveFile={handleBusinessLicenseRemove}
          error={
            businessLicenseUploadError ??
            (errors.business_license_document?.message as TranslationKeys | undefined)
          }
        />
      </View>
    </StepShell>
  );
}
